import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getProjectHistory, projectFileBytes, parseFileCard } from '../transport/api'
import { findProjectFileId } from './projectFileResolve'
import type { ProjectFileRef } from './projectFileNotice'
import { Icon } from '../ui/Icon'
import { useBlobUrl } from '../ui/useBlobUrl'
import { ImageTile, imageMime } from '../ui/ImageTile'

/**
 * A file shared into a project, rendered inline where its notice appears — in an
 * AGENT's transcript (Clean View) and now in PROJECT CHAT too (owner, 2026-08-14:
 * project chat 裡面遇到檔案標籤可以解析顯示預覽圖片). Extracted from AgentPanel so
 * both surfaces show the same picture from the same shared history query.
 *
 * The notice names a PATH built for humans — `~/project-files/<id>/<name>` — so the
 * project id is recoverable and the file id is NOT; `/project/file/blob` needs both,
 * which is why this costs a lookup into the project's own history. MOST RECENT MATCH
 * WINS: `_agent_blob_link` repoints a re-shared filename at the newer file, so any
 * other choice would show a picture the sharer was not looking at.
 *
 * `onOpen` opens the cover-panel FileViewer instead of a new tab (the same overlay
 * the agent terminal uses) — the click behaviour the owner asked to unify.
 */
export function ProjectImage({
  pf,
  onOpen,
}: {
  pf: ProjectFileRef
  /** Opens the cover-panel viewer. Omitted, the tile asks the OpenDoc context —
   *  Clean View provides one since 2026-09-13 — and only with neither does the
   *  picture open in a new tab. */
  onOpen?: (a: { url: string; name: string; mime: string }) => void
}) {
  const [failed, setFailed] = useState(false)
  const hist = useQuery({
    queryKey: ['project-history', pf.projectId],
    queryFn: () => getProjectHistory(pf.projectId),
    staleTime: 60_000,
  })
  const inPage = (() => {
    const msgs = hist.data?.messages ?? []
    // Newest first: the history is oldest-first, so walk it backwards.
    for (let i = msgs.length - 1; i >= 0; i--) {
      const card = parseFileCard(msgs[i]!.body)
      const hit = card?.attachments?.find((a) => a.name === pf.name)
      if (hit) return hit.file_id
    }
    return null
  })()

  /*
   * THE PAGE ABOVE IS THE NEWEST 50 MESSAGES, and a file older than that is still a
   * file. Missing it, this used to say "not found in this project" about something that
   * was plainly in the project — a statement that got more wrong the longer a project
   * stayed alive, so the only projects whose previews worked were the quiet ones.
   *
   * Runs ONLY on a miss, and only when the history says there is more behind the page
   * (`has_more`), so the ordinary case — a file shared recently — costs no extra request
   * at all. Keyed by name rather than by page so every notice for the same file shares
   * one walk, and cached longer than the history: a file id, once resolved, does not
   * change (a re-share writes a NEW message, which lands in the page above and wins
   * there before this query is ever consulted).
   */
  const deep = useQuery({
    queryKey: ['project-file-id', pf.projectId, pf.name],
    queryFn: () => findProjectFileId(pf.projectId, pf.name, hist.data!.oldest!),
    enabled: !inPage && hist.data?.has_more === true && hist.data?.oldest != null,
    staleTime: 300_000,
  })

  const fileId = inPage ?? deep.data?.fileId ?? null

  // Unresolved is not the same as failed, and neither is the same as loading. A
  // locked vault answers the history query with an error, and claiming "not found"
  // there would send someone looking for a file that is exactly where they left it.
  if (!fileId || failed) {
    /*
     * Three different things, said as three different sentences. The last one is the
     * point of the walk above: when the BOUND stopped it rather than the end of the
     * history, the honest answer names the window it searched instead of denying the
     * file exists.
     */
    const why = hist.isPending
      ? ' — loading…'
      : failed
        ? ' — can’t load it'
        : deep.isFetching
          ? ' — looking further back…'
          : deep.data && !deep.data.exhausted
            ? ` — not in the last ${deep.data.scanned + (hist.data?.messages.length ?? 0)} messages`
            : ' — not found in this project'
    return (
      <span className="msg-file off" title={pf.path}>
        <Icon name="attach_file" size={13} />
        {pf.name}
        {why}
      </span>
    )
  }
  return (
    <ProjectImageBytes
      projectId={pf.projectId}
      fileId={fileId}
      name={pf.name}
      onFail={() => setFailed(true)}
      {...(onOpen ? { onOpen } : {})}
    />
  )
}

/** The picture itself, fetched as base64 slices over the relay — the blob URL
 *  points at the page origin, which proxies nothing of the edge's. Clicking opens
 *  the cover-panel viewer, not a new tab (ImageTile decides which viewer). */
export function ProjectImageBytes({
  projectId,
  fileId,
  name,
  onOpen,
  onFail,
}: {
  projectId: string
  fileId: string
  name: string
  onOpen?: (a: { url: string; name: string; mime: string }) => void
  onFail: () => void
}) {
  const { url, failed } = useBlobUrl(() => projectFileBytes(projectId, fileId), [projectId, fileId])
  // The parent owns the failed state (an <img> decode error reports through the same
  // prop), so the fetch failure is forwarded rather than kept here.
  useEffect(() => {
    if (failed) onFail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failed])
  if (!url) return <span className="msg-file off">{name} — loading…</span>
  return (
    <ImageTile url={url} name={name} mime={imageMime(name)} onError={onFail} {...(onOpen ? { onOpen } : {})} />
  )
}
